import { useMutation } from '@apollo/client';
import React from 'react';
import AddMemberControl from './AddMemberControl';
import {
  GroupPolicyAction,
  isUserInGroup,
  useGroupPolicyCheck,
} from './groupPolicyCheck';
import GroupSubgroupCard from './GroupSubgroupCard';
import GroupUserCard from './GroupUserCard';
import * as styles from './useGroupMembersColumn.module.scss';
import useAppSelector from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import type { GroupUserRole } from '../graphql/generated/graphql';

gql(`
  fragment useGroupMembersColumn_member on User {
    ...GroupUserCard_member
  }
`);

const USE_GROUP_MEMBERS_COLUMN_GROUP_FRAGMENT = gql(`
  fragment useGroupMembersColumn_group on Group {
    id
    owners {
      id
      ...useGroupMembersColumn_member
    }
    members {
      id
      ...useGroupMembersColumn_member
    }
    children {
      ...GroupSubgroupCard_group
    }
    ...AddMemberControl_group
    ...useGroupPolicyCheck_group
  }

`);

const ADD_GROUP_TO_GROUP_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupMembersColumnAddGroupToGroup($parentId: ID!, $childId: ID!) {
    addGroupToGroup(parentId: $parentId, childId: $childId) {
      id
      children {
        id
      }
    }
  }
`);

const REMOVE_GROUP_FROM_GROUP_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupMembersColumnRemoveGroupFromGroup(
    $parentId: ID!
    $childId: ID!
  ) {
    removeGroupFromGroup(parentId: $parentId, childId: $childId) {
      id
      children {
        id
      }
    }
  }
`);

function useGroupMembersColumn(
  group:
    | DocumentType<typeof USE_GROUP_MEMBERS_COLUMN_GROUP_FRAGMENT>
    | undefined,
  handleAddUser: (id: string) => Promise<unknown>,
  handleUpdateUserRole: (id: string, role: GroupUserRole) => Promise<unknown>,
  handleRemoveUser: (id: string) => Promise<unknown>,
) {
  const owners = group?.owners || [];
  const members = group?.members || [];
  const children = group?.children || [];
  const hasCards = owners.length + members.length + children.length > 0;

  const [addToGroupMutate] = useMutation(ADD_GROUP_TO_GROUP_MUTATION);
  const handleAddSubgroup = (childId: string) =>
    addToGroupMutate({
      variables: {
        parentId: group!.id,
        childId,
      },
    });

  const [removeFromGroupMutate] = useMutation(REMOVE_GROUP_FROM_GROUP_MUTATION);
  const handleRemoveSubgroup = (childId: string) =>
    removeFromGroupMutate({
      variables: {
        parentId: group!.id,
        childId,
      },
    });

  const canEditMembership = useGroupPolicyCheck(
    GroupPolicyAction.EditMembership,
    group,
  );

  const currentUser = useAppSelector((state) => state.session.user);

  if (!group) {
    return {};
  }

  const isShowingMembershipButton = isUserInGroup(group, currentUser);

  const getUserEditingProps = (id: string) =>
    canEditMembership ||
    (currentUser.type === UserType.Admin && id === currentUser.id)
      ? {
          onChangeRole: handleUpdateUserRole.bind(null, id),
          onRemove: handleRemoveUser.bind(null, id),
        }
      : {};

  const mainContent = hasCards ? (
    <div className={styles.usersGrid}>
      {owners.map(
        (owner) =>
          owner?.id && (
            <GroupUserCard
              key={owner.id}
              user={owner}
              owner
              {...getUserEditingProps(owner.id)}
            />
          ),
      )}
      {members.map(
        (member) =>
          member?.id && (
            <GroupUserCard
              key={member.id}
              user={member}
              {...getUserEditingProps(member.id)}
            />
          ),
      )}
      {children.map(
        (child) =>
          child?.id && (
            <GroupSubgroupCard
              key={child.id}
              group={child}
              onRemove={
                canEditMembership && child?.id
                  ? () => handleRemoveSubgroup(child.id)
                  : undefined
              }
            />
          ),
      )}
    </div>
  ) : (
    <EmptyState
      imageName="connections"
      title="No members yet..."
      message="Members will appear after they have been added"
    />
  );

  return {
    title: 'Members',
    controls: isShowingMembershipButton ? (
      <AddMemberControl
        group={group}
        canEdit={canEditMembership}
        onAddUser={(id: string) => handleAddUser(id)}
        onAddGroup={(id: string) => handleAddSubgroup(id)}
      />
    ) : null,
    children: mainContent,
  };
}

export default useGroupMembersColumn;
